import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Row, Input, Col, Collapse } from 'antd';
import { Controller, useFieldArray } from 'react-hook-form';

import { CloseOutlined } from '@ant-design/icons';
import GetField from '../Components/GetField';
import stylesCSS from '../styles';
import FabricForm from './FabricForm';
import PositionForm from './PositionForm';
import ProcessForm from './ProcessForm';
import {
  getTotal,
  getProductTotalA,
  getProductTotalB,
  getProductTotalC,
} from './common-functions';

const { Panel } = Collapse;

const AntInput = GetField(Input);

const StyleForm = props => {
  const {
    classes,
    getValues,
    control,
    setValue,
    watch,
    styleItem,
    styleIndex,
    styleRemove,
  } = props;

  const {
    fields: fabricFields,
    append: fabricAppend,
    remove: fabricRemove,
  } = useFieldArray({
    control,
    name: `styles[${styleIndex}].fabrics`,
  });

  const {
    fields: positionFields,
    append: positionAppend,
    remove: positionRemove,
  } = useFieldArray({
    control,
    name: `styles[${styleIndex}].positions`,
  });

  const {
    fields: processFields,
    append: processAppend,
    remove: processRemove,
  } = useFieldArray({
    control,
    name: `styles[${styleIndex}].processes`,
  });

  useEffect(() => {
    const fabrics = getValues(`styles[${styleIndex}].fabrics`);
    const positions = getValues(`styles[${styleIndex}].positions`);
    const processes = getValues(`styles[${styleIndex}].processes`);

    setValue(
      `styles[${styleIndex}].total`,
      getTotal(fabrics, positions, processes)
    );
  }, [fabricFields.length, positionFields.length, processFields.length]);

  useEffect(() => {
    const total = getValues(`styles[${styleIndex}].total`) || 0;
    const profitA = getValues(`styles[${styleIndex}].profitA`) || 0;
    const profitB = getValues(`styles[${styleIndex}].profitB`) || 0;
    const profitC = getValues(`styles[${styleIndex}].profitC`) || 0;
    const commision = getValues(`styles[${styleIndex}].commision`) || 0;
    const exchangeRate = getValues(`styles[${styleIndex}].exchangeRate`) || 1;

    const _priceA = Math.round(+total * (1 + +profitA * 0.01) * 100) / 100;
    if (
      total &&
      profitA !== 0 &&
      profitA !== '' &&
      profitA !== null &&
      profitA !== undefined
    ) {
      setValue(`styles[${styleIndex}].priceA`, +_priceA);
    } else {
      setValue(`styles[${styleIndex}].priceA`, 0);
    }

    const _priceB = Math.round(+total * (1 + +profitB * 0.01) * 100) / 100;
    if (
      total &&
      profitB !== 0 &&
      profitB !== '' &&
      profitB !== null &&
      profitB !== undefined
    ) {
      setValue(`styles[${styleIndex}].priceB`, +_priceB);
    } else {
      setValue(`styles[${styleIndex}].priceB`, 0);
    }

    const _priceC = Math.round(+total * (1 + +profitC * 0.01) * 100) / 100;
    if (
      total &&
      profitC !== 0 &&
      profitC !== '' &&
      profitC !== null &&
      profitC !== undefined
    ) {
      setValue(`styles[${styleIndex}].priceC`, +_priceC);
    } else {
      setValue(`styles[${styleIndex}].priceC`, 0);
    }

    if (
      total &&
      profitA !== 0 &&
      profitA !== '' &&
      profitA !== null &&
      profitA !== undefined &&
      exchangeRate &&
      commision + 1
    ) {
      let _totalA = _priceA / +exchangeRate;
      _totalA /= 1 - +commision * 0.01;
      _totalA = Math.round(_totalA * 100) / 100;
      setValue(`styles[${styleIndex}].totalA`, +_totalA);
    } else {
      setValue(`styles[${styleIndex}].totalA`, 0);
    }

    if (
      total &&
      profitB !== 0 &&
      profitB !== '' &&
      profitB !== null &&
      profitB !== undefined &&
      exchangeRate &&
      commision + 1
    ) {
      let _totalB = _priceB / +exchangeRate;
      _totalB /= 1 - +commision * 0.01;
      _totalB = Math.round(_totalB * 100) / 100;
      setValue(`styles[${styleIndex}].totalB`, +_totalB);
    } else {
      setValue(`styles[${styleIndex}].totalB`, 0);
    }

    if (
      total &&
      profitC !== 0 &&
      profitC !== '' &&
      profitC !== null &&
      profitC !== undefined &&
      exchangeRate &&
      commision + 1
    ) {
      let _totalC = _priceC / +exchangeRate;
      _totalC /= 1 - +commision * 0.01;
      _totalC = Math.round(_totalC * 100) / 100;

      setValue(`styles[${styleIndex}].totalC`, +_totalC);
    } else {
      setValue(`styles[${styleIndex}].totalC`, 0);
    }

    const styles = getValues(`styles`);

    setValue(`productTotalA`, getProductTotalA(styles));
    setValue(`productTotalB`, getProductTotalB(styles));
    setValue(`productTotalC`, getProductTotalC(styles));
  }, [
    watch(`styles[${styleIndex}].total`),
    watch(`styles[${styleIndex}].profitA`),
    watch(`styles[${styleIndex}].profitB`),
    watch(`styles[${styleIndex}].profitC`),
    watch(`styles[${styleIndex}].priceA`),
    watch(`styles[${styleIndex}].priceB`),
    watch(`styles[${styleIndex}].priceC`),
    watch(`styles[${styleIndex}].commision`),
    watch(`styles[${styleIndex}].exchangeRate`),
  ]);

  return (
    <>
      <Collapse key={styleIndex}>
        <Panel
          header={`Style ${styleIndex + 1}`}
          key="1"
          extra={
            <CloseOutlined
              onClick={() => {
                const styles = getValues('styles');
                if (styles && styles.length > 1) {
                  styleRemove(styleIndex);
                }
              }}
            />
          }
        >
          <Row gutter={[8, 0]}>
            <Col xs={12}>
              <Controller
                name={`styles[${styleIndex}].description`}
                control={control}
                defaultValue={styleItem.description}
                render={({ field }) => (
                  <AntInput
                    label="Description"
                    placeholder="Description"
                    {...field}
                  />
                )}
              />
            </Col>
          </Row>
          <Row>
            <table className={classes.inputTable}>
              <thead className={classes.inputThead}>
                <tr key="header">
                  <th className={classes.inputTh}>Fabric</th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Width
                  </th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Base Color
                  </th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Print Color
                  </th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Greige Price
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    GST (%)
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Amount
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Shrinkage (%)
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Dying
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Printing
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Total
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {fabricFields.map((item, index) => (
                  <FabricForm
                    key={item.id}
                    fabricItem={item}
                    fabricIndex={index}
                    fabricAppend={fabricAppend}
                    fabricRemove={fabricRemove}
                    getValues={getValues}
                    setValue={setValue}
                    control={control}
                    watch={watch}
                    styleIndex={styleIndex}
                  />
                ))}
              </tbody>
            </table>
          </Row>
          <br />
          <Row>
            <table className={classes.inputTable} style={{ width: '70%' }}>
              <thead className={classes.inputThead}>
                <tr key="header">
                  <th className={classes.inputTh}>Position</th>
                  <th className={classes.inputTh}>Fabric</th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Price
                  </th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Avg
                  </th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Total
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {positionFields.map((item, index) => (
                  <PositionForm
                    key={item.id}
                    positionItem={item}
                    positionIndex={index}
                    positionAppend={positionAppend}
                    positionRemove={positionRemove}
                    getValues={getValues}
                    setValue={setValue}
                    control={control}
                    watch={watch}
                    styleIndex={styleIndex}
                  />
                ))}
              </tbody>
            </table>
          </Row>
          <br />
          <Row>
            <table className={classes.inputTable} style={{ width: '60%' }}>
              <thead className={classes.inputThead}>
                <tr key="header">
                  <th className={classes.inputTh}>Process</th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Qty/Avg
                  </th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Price
                  </th>
                  <th className={classes.inputTh} style={{ width: '8%' }}>
                    Total
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {processFields.map((item, index) => (
                  <ProcessForm
                    key={item.id}
                    processItem={item}
                    processIndex={index}
                    processAppend={processAppend}
                    processRemove={processRemove}
                    getValues={getValues}
                    setValue={setValue}
                    control={control}
                    watch={watch}
                    styleIndex={styleIndex}
                  />
                ))}
              </tbody>
            </table>
          </Row>
          <br />
          <Row gutter={[48, 0]}>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].total`}
                control={control}
                defaultValue={styleItem.total}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Total"
                    placeholder="Total"
                    type="number"
                    min={0}
                    disabled
                  />
                )}
              />
            </Col>
          </Row>
          <Row gutter={[48, 0]}>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].profitA`}
                control={control}
                defaultValue={styleItem.profitA}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Profit A (%)"
                    placeholder="Profit A (%)"
                    type="number"
                    min={0}
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].profitB`}
                control={control}
                defaultValue={styleItem.profitB}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Profit B (%)"
                    placeholder="Profit B (%)"
                    type="number"
                    min={0}
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].profitC`}
                control={control}
                defaultValue={styleItem.profitC}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Profit C (%)"
                    placeholder="Profit C (%)"
                    type="number"
                    min={0}
                  />
                )}
              />
            </Col>
          </Row>
          <Row gutter={[48, 0]}>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].priceA`}
                control={control}
                defaultValue={styleItem.priceA}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Price A"
                    placeholder="Price A"
                    type="number"
                    min={0}
                    disabled
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].priceB`}
                control={control}
                defaultValue={styleItem.priceB}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Price B"
                    placeholder="Price B"
                    type="number"
                    min={0}
                    disabled
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].priceC`}
                control={control}
                defaultValue={styleItem.priceC}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Price C"
                    placeholder="Price C"
                    type="number"
                    min={0}
                    disabled
                  />
                )}
              />
            </Col>
          </Row>
          <Row gutter={[48, 0]}>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].currency`}
                control={control}
                defaultValue={styleItem.currency}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Currency"
                    placeholder="Currency"
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].commision`}
                control={control}
                defaultValue={styleItem.commision}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Commision (%)"
                    placeholder="Commision (%)"
                    type="number"
                    min={0}
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].exchangeRate`}
                control={control}
                defaultValue={styleItem.exchangeRate}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Exchange Rate"
                    placeholder="Exchange Rate"
                    type="number"
                    min={0}
                  />
                )}
              />
            </Col>
          </Row>
          <Row gutter={[48, 0]}>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].totalA`}
                control={control}
                defaultValue={styleItem.totalA}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Total A"
                    placeholder="Total A"
                    type="number"
                    min={0}
                    disabled
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].totalB`}
                control={control}
                defaultValue={styleItem.totalB}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Total B"
                    placeholder="Total B"
                    type="number"
                    min={0}
                    disabled
                  />
                )}
              />
            </Col>
            <Col span={4}>
              <Controller
                name={`styles[${styleIndex}].totalC`}
                control={control}
                defaultValue={styleItem.totalC}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    label="Total C"
                    placeholder="Total C"
                    type="number"
                    min={0}
                    disabled
                  />
                )}
              />
            </Col>
          </Row>
          <Row gutter={[48, 0]}>
            <Col span={12}>
              <Controller
                name={`styles[${styleIndex}].remark`}
                control={control}
                defaultValue={styleItem.remark}
                render={({ field }) => (
                  <AntInput label="Remark" placeholder="Remark" {...field} />
                )}
              />
            </Col>
          </Row>
        </Panel>
      </Collapse>
    </>
  );
};

StyleForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  styleItem: PropTypes.object.isRequired,
  styleIndex: PropTypes.number.isRequired,
  styleRemove: PropTypes.func.isRequired,
};

export default withStyles(stylesCSS)(StyleForm);
